import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from '../hooks/use-toast';
import { useAuth } from '../hooks/useAuth';
import { useWorkspaces } from '../contexts/WorkspaceContext';
import { captureError } from '../lib/sentry';

interface InvitationDetails {
  id: string;
  invitee_email: string;
  member_role: string;
  workspace?: { id: string; name: string };
  inviter?: { full_name?: string; email?: string };
}

export default function AcceptInvitation() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { refetch: refetchWorkspaces } = useWorkspaces();
  const [isProcessing, setIsProcessing] = useState(false);
  const [invitationData, setInvitationData] = useState<InvitationDetails | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [emailMismatch, setEmailMismatch] = useState<{
    inviteeEmail: string;
    currentEmail: string;
  } | null>(null);

  useEffect(() => {
    const token = searchParams.get('token');

    if (!token) {
      setError('Invalid invitation link');
      return;
    }

    // Fetching invitation details requires an authenticated user, since the
    // edge function forwards the caller's JWT into the RPC for RLS enforcement.
    if (!user) {
      // Persist the full URL so post-login redirect can come back here.
      sessionStorage.setItem(
        'pendingInvitationRedirect',
        window.location.pathname + window.location.search,
      );
      return;
    }

    fetchInvitationDetails(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, user]);

  const fetchInvitationDetails = async (encryptedToken: string) => {
    try {
      const { data: result, error: fnError } = await supabase.functions.invoke(
        'validate-invitation',
        { body: { encrypted_token: encryptedToken } },
      );

      if (fnError) {
        console.error('[AcceptInvitation] validate-invitation invoke error:', fnError);
        setError('Invalid or expired invitation link');
        return;
      }

      if (!result?.success) {
        // validate-invitation reliably returns error_code: 'EMAIL_MISMATCH'
        // with invitee_email/current_email when the caller's email doesn't
        // match the invitation. Use the structured fields directly.
        if (result?.error_code === 'EMAIL_MISMATCH') {
          setEmailMismatch({
            inviteeEmail: result?.invitee_email || '',
            currentEmail: result?.current_email || user?.email || '',
          });
          return;
        }
        setError(result?.error || 'Invalid or expired invitation link');
        return;
      }

      const invitation = result.invitation as InvitationDetails;

      // Defense-in-depth: catch the mismatch client-side too so we always
      // render the dedicated UI even if the RPC returns success.
      if (
        invitation?.invitee_email &&
        user?.email &&
        invitation.invitee_email.toLowerCase() !== user.email.toLowerCase()
      ) {
        setEmailMismatch({
          inviteeEmail: invitation.invitee_email,
          currentEmail: user.email,
        });
        return;
      }

      setInvitationData(invitation);
    } catch (err) {
      console.error('[AcceptInvitation] Error fetching invitation:', err);
      setError('Failed to load invitation details');
    }
  };

  const handleAcceptInvitation = async () => {
    if (!invitationData || !user) return;

    setIsProcessing(true);
    try {
      const { data: result, error: rpcError } = await supabase
        .rpc('accept_workspace_invitation', { p_invitation_id: invitationData.id });

      if (rpcError || !result?.success) {
        throw new Error(result?.error || rpcError?.message || 'Failed to accept invitation');
      }

      // Refetch workspace list so the newly-joined workspace appears in the
      // dropdown immediately (otherwise user has to refresh 1-2 times).
      try {
        await refetchWorkspaces();
      } catch (_e) {
        // refetch is a nice-to-have; don't block navigation on it
      }

      toast({
        description: `Successfully joined ${invitationData.workspace?.name ?? 'the workspace'}`,
      });

      navigate('/contacts');
    } catch (err) {
      console.error('Accept invitation error:', err);
      captureError(err as Error, { feature: 'accept-invitation' });
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to join workspace',
        variant: 'destructive',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRejectInvitation = async () => {
    if (!invitationData) return;

    setIsProcessing(true);
    try {
      const { data: result, error: rpcError } = await supabase
        .rpc('reject_workspace_invitation', { p_invitation_id: invitationData.id });

      if (rpcError || !result?.success) {
        throw new Error(result?.error || rpcError?.message || 'Failed to reject invitation');
      }

      toast({ description: 'Invitation declined' });
      navigate('/');
    } catch (_err) {
      captureError(_err as Error, { feature: 'accept-invitation' });
      toast({
        title: 'Error',
        description: 'Failed to decline invitation',
        variant: 'destructive',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSwitchAccount = async () => {
    // Persist the current URL so the post-login redirect drops the user back
    // here automatically once they sign in with the correct account.
    sessionStorage.setItem(
      'pendingInvitationRedirect',
      window.location.pathname + window.location.search,
    );
    try {
      await supabase.auth.signOut();
    } catch (err) {
      console.error('[AcceptInvitation] Sign out error:', err);
    }
    navigate('/login');
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-red-600">Invalid Invitation</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 mb-4">{error}</p>
            <Button onClick={() => navigate('/')} variant="outline" className="w-full">
              Go to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Wrong-account UX — surfaced before the generic error fallback.
  if (emailMismatch) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Wrong Account</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 mb-4">
              This invitation was sent to{' '}
              <strong>{emailMismatch.inviteeEmail || 'a different email address'}</strong>
              {emailMismatch.currentEmail ? (
                <>
                  , but you are signed in as <strong>{emailMismatch.currentEmail}</strong>.
                </>
              ) : (
                '.'
              )}{' '}
              Sign out to continue with the correct account.
            </p>
            <Button onClick={handleSwitchAccount} className="w-full mb-2">
              Sign out and accept invitation
            </Button>
            <Button onClick={() => navigate('/')} variant="outline" className="w-full">
              Cancel
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Sign-in prompt for unauthenticated visitors.
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Sign In Required</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 mb-4">
              You need to sign in to accept this workspace invitation.
            </p>
            <Button
              onClick={() => {
                sessionStorage.setItem(
                  'pendingInvitationRedirect',
                  window.location.pathname + window.location.search,
                );
                navigate('/login');
              }}
              className="w-full mb-2"
            >
              Sign In
            </Button>
            <Button
              onClick={() => {
                sessionStorage.setItem(
                  'pendingInvitationRedirect',
                  window.location.pathname + window.location.search,
                );
                navigate('/login?mode=signup');
              }}
              variant="outline"
              className="w-full"
            >
              Create Account
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Loading state while we fetch invitation details.
  if (!invitationData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading invitation...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Workspace Invitation</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="text-center">
              <h3 className="text-lg font-semibold">{invitationData.workspace?.name}</h3>
              <p className="text-gray-600">
                You've been invited by{' '}
                {invitationData.inviter?.full_name || invitationData.inviter?.email}{' '}
                to join as a {invitationData.member_role}.
              </p>
            </div>

            <div className="flex space-x-3">
              <Button
                onClick={handleAcceptInvitation}
                disabled={isProcessing}
                className="flex-1"
              >
                {isProcessing ? 'Processing...' : 'Accept Invitation'}
              </Button>

              <Button
                onClick={handleRejectInvitation}
                disabled={isProcessing}
                variant="outline"
                className="flex-1"
              >
                Decline
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
